import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  VStack,
  SimpleGrid,
  Heading,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Tag,
  TagLabel,
  HStack,
  Link,
  Image,
  Wrap,
  WrapItem,
  Badge,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaSearch, FaGlobe, FaInstagram, FaLinkedin, FaPlay } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';
import ScrollIndicator from '../components/ScrollIndicator';
import PageHero from '../components/PageHero';
import guestsData from '../data/guests.json';

const getCustomImageStyles = (id) => {
  switch(id) {
    case 'seema-anand':
      return {
        objectPosition: 'center 30%'
      };
    case 'anand-neelakantan':
      return {
        objectPosition: '70% center'
      };
    case 'kavita-kane':
      return {
        objectPosition: 'center 30%'
      };
    default:
      return {
        objectPosition: 'center'
      };
  }
};

const GuestCard = ({ guest, delay = 0 }) => (
  <ScrollReveal delay={delay}>
    <Box
      layerStyle="glassCard"
      p={{ base: 5, md: 6 }}
      position="relative"
      overflow="hidden"
      _hover={{
        borderColor: 'rgba(255,152,0,0.35)',
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 60px rgba(255,152,0,0.12)',
      }}
      transition="all 0.3s"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="2px"
        bgGradient="linear(to-r, transparent, brand.500, transparent)"
      />
      <VStack spacing={{ base: 4, md: 6 }} align="stretch" flex="1">
        {/* Header with Image */}
        <HStack spacing={{ base: 4, md: 6 }} align="flex-start">
          <Box
            flexShrink={0}
            width={{ base: "80px", md: "100px" }}
            height={{ base: "80px", md: "100px" }}
            borderRadius="full"
            overflow="hidden"
            border="2px solid rgba(255,152,0,0.35)"
            boxShadow="0 0 16px rgba(255,152,0,0.15)"
          >
            <Image
              src={guest.image}
              alt={guest.name}
              width="100%"
              height="100%"
              objectFit="cover"
              {...getCustomImageStyles(guest.id)}
              fallback={<Box w="100%" h="100%" bg="rgba(255,150,50,0.1)" />}
            />
          </Box>
          <VStack align="start" spacing={1} flex="1">
            <Heading
              as="h3"
              fontSize={{ base: "lg", md: "xl" }}
              color="whiteAlpha.900"
            >
              {guest.name}
            </Heading>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="brand.300"
              fontWeight="medium"
            >
              {guest.title}
            </Text>
            <HStack spacing={3} mt={{ base: 1, md: 2 }}>
              {guest.social?.linkedin && (
                <Link
                  href={guest.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="whiteAlpha.900"
                  _hover={{ 
                    color: 'brand.300',
                    transform: 'translateY(-1px)'
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FaLinkedin} boxSize={{ base: 4, md: 5 }} />
                </Link>
              )}
              {guest.social?.twitter && (
                <Link
                  href={guest.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="whiteAlpha.900"
                  _hover={{ 
                    color: 'brand.300',
                    transform: 'translateY(-1px)'
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FaXTwitter} boxSize={{ base: 4, md: 5 }} />
                </Link>
              )}
              {guest.social?.instagram && (
                <Link
                  href={guest.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="whiteAlpha.900"
                  _hover={{ 
                    color: 'brand.300',
                    transform: 'translateY(-1px)'
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FaInstagram} boxSize={{ base: 4, md: 5 }} />
                </Link>
              )}
              {guest.social?.website && (
                <Link
                  href={guest.social.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="whiteAlpha.900"
                  _hover={{ 
                    color: 'brand.300',
                    transform: 'translateY(-1px)'
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FaGlobe} boxSize={{ base: 4, md: 5 }} />
                </Link>
              )}
            </HStack>
          </VStack>
        </HStack>

        {/* Bio */}
        <Box w="100%" h="1px" bg="rgba(255,150,50,0.1)" />
        <Text
          fontSize={{ base: "sm", md: "md" }}
          color="whiteAlpha.800"
          lineHeight="tall"
        >
          {guest.bio}
        </Text>

        {/* Expertise */}
        <Box>
          <Text
            color="whiteAlpha.700"
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="medium"
            mb={2}
          >
            Areas of Expertise
          </Text>
          <Wrap spacing={{ base: 1, md: 2 }}>
            {guest.expertise.map((area, index) => (
              <WrapItem key={index}>
                <Tag
                  size={{ base: "sm", md: "md" }}
                  colorScheme="brand"
                  variant="subtle"
                >
                  <TagLabel>{area}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Box>

        {/* Episodes */}
        <Box>
          <Text
            color="whiteAlpha.700"
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="medium"
            mb={2}
          >
            Featured Episodes
          </Text>
          <VStack align="stretch" spacing={2}>
            {guest.episodes.map((episode, index) => (
              <RouterLink
                key={index}
                to={`/episodes?search=${encodeURIComponent(episode.title)}`}
              >
                <HStack
                  spacing={2}
                  p={{ base: 2, md: 3 }}
                  layerStyle="glassTile"
                  borderRadius="md"
                  _hover={{
                    background: 'rgba(255,152,0,0.1)',
                    borderColor: 'rgba(255,152,0,0.25)',
                    transform: 'translateX(4px)'
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FaPlay} boxSize={{ base: 3, md: 4 }} color="brand.500" />
                  <Text 
                    color="whiteAlpha.900" 
                    fontSize={{ base: "xs", md: "sm" }} 
                    flex="1"
                    noOfLines={1}
                  >
                    {episode.title}
                  </Text>
                  {episode.parts > 1 && (
                    <Badge
                      colorScheme="orange"
                      fontSize={{ base: "xs", md: "sm" }}
                      px={1.5}
                    >
                      {episode.parts} parts
                    </Badge>
                  )}
                  <Text fontSize="xs" color="whiteAlpha.400">→</Text>
                </HStack>
              </RouterLink>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  </ScrollReveal>
);

const Guests = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGuests = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return guestsData.guests.filter(guest =>
      guest.name.toLowerCase().includes(query) ||
      guest.title.toLowerCase().includes(query) ||
      guest.bio.toLowerCase().includes(query) ||
      guest.expertise.some(area => area.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  return (
    <>
      <Box minH="100vh" pt={{ base: 20, md: 20 }} pb={{ base: 12, md: 20 }} id="guests-container">
        <SEO
          title="Our Guests"
          description="Meet the amazing guests who have shared their knowledge and stories on Namaskar India."
          keywords="podcast guests, experts, historians, storytellers, indian history and mythology"
        />

        <Container maxW="container.xl">
          <VStack spacing={{ base: 8, md: 10 }} align="stretch">
            {/* Header */}
            <PageHero
              title="Our Distinguished Guests"
              subtitle="Meet the experts and thought leaders who bring their unique perspectives to our podcast."
            />

            {/* Search Bar */}
            <Box maxW="2xl" mx="auto" w="full" px={{ base: 4, md: 0 }}>
              <Box position="relative" overflow="hidden" borderRadius="lg">
                <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" zIndex={1} />
                <InputGroup size={{ base: "md", md: "lg" }}>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="whiteAlpha.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search guests by name, expertise..."
                    aria-label="Search guests"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    background="rgba(255,150,50,0.05)"
                    backdropFilter="blur(12px)"
                    borderColor="rgba(255,150,50,0.12)"
                    _hover={{ borderColor: "rgba(255,152,0,0.3)" }}
                    _focus={{ borderColor: "rgba(255,152,0,0.5)", boxShadow: "0 0 0 1px rgba(255,152,0,0.2)", bg: "rgba(255,150,50,0.09)" }}
                    color="whiteAlpha.900"
                    _placeholder={{ color: "whiteAlpha.400" }}
                  />
                </InputGroup>
              </Box>
            </Box>

            {/* Guest Cards */}
            {filteredGuests.length === 0 ? (
              <VStack spacing={3} py={16} textAlign="center">
                <Text fontSize={{ base: "lg", md: "xl" }} color="whiteAlpha.600">
                  No guests found for "{searchQuery}"
                </Text>
                <Box
                  as="button"
                  onClick={() => setSearchQuery('')}
                  fontSize="sm"
                  color="brand.400"
                  _hover={{ color: 'brand.300' }}
                  transition="color 0.15s"
                >
                  Clear search
                </Box>
              </VStack>
            ) : (
              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={{ base: 6, md: 8 }}
                px={{ base: 4, md: 0 }}
              >
                {filteredGuests.map((guest, index) => (
                  <GuestCard key={guest.id} guest={guest} delay={Math.min(index * 0.06, 0.3)} />
                ))}
              </SimpleGrid>
            )}
          </VStack>
        </Container>
        <ScrollIndicator containerId="guests-container" />
      </Box>
    </>
  );
};

export default Guests;
